'use client';

import HeadingWithTools from '@/components/ui/HeadingWithTools';
import Well from '@/components/ui/Well';
import ListMissions from '@/components/ui/ListMissions';
import * as React from 'react';
import { CmsParams } from '@/lib/cms-params.type';
import { JackpotPurchaseTicketItem } from '@/components/jackpot/jackpot-purchase-tickets/jackpot-purchase-ticket-item';
import { FrontTicketPurchaseOfferResponseDto, FrontTicketPurchaseTicketStatsResponseDto } from '@/lib/jp-api/requests';
import { CurrencySelector } from '@/components/currency-selector/currency-selector';
import { isAuthenticated } from '@/lib/helper/is-authenticated';
import { TicketBalance } from '@/components/jackpot/jackpot-purchase-tickets/ticket-balance';
import { useOverlay } from '@/components/hooks/useOverlay';
import { useTranslations } from '@/lib/hooks/useTranslations';
import { OverlayPurchaseTicketsMeta } from '@/components/overlays/overlays';
import { integerFormatter } from '@/lib/formatter/integer-formatter';
import { IMissionTheme } from '@/components/ui/ListMissionsItem';

export function getOfferHighlight(
  offer: FrontTicketPurchaseOfferResponseDto,
  offers: FrontTicketPurchaseOfferResponseDto[],
  bestValue: string,
) {
  if (offer.highlight) return offer.highlight;
  if (offer === offers[0]) return bestValue;
  return offer.highlight;
}

export type PurchaseTicket = {
  id: string;
  image: string;
  label: string;
  theme: IMissionTheme;
  illustrationImage: string;
  stats: FrontTicketPurchaseTicketStatsResponseDto;
};

type JackpotPurchaseTicketsClientProps = {
  ticket: PurchaseTicket;
  offers: FrontTicketPurchaseOfferResponseDto[];
  baseOffer: FrontTicketPurchaseOfferResponseDto;
  cmsParams: CmsParams;
  ns?: string;
  ticketsInPool: number;
};

export const JackpotPurchaseTicketsClient = ({
  cmsParams,
  ticket,
  ns = 'default',
  baseOffer,
  offers,
  ticketsInPool,
}: JackpotPurchaseTicketsClientProps) => {
  const t = useTranslations(`JackpotPurchaseTickets`, ns);
  const isUserAuthenticated = isAuthenticated(cmsParams);
  const { openOverlay } = useOverlay<OverlayPurchaseTicketsMeta>(`purchase-tickets`);

  return (
    <React.Fragment>
      <HeadingWithTools
        id="ticket-offers"
        title={t('title')}
        description={t('description')}
        addOn={
          isUserAuthenticated ? (
            <ul className="tickets-tools">
              <li>
                <TicketBalance
                  cmsParams={cmsParams}
                  ticketId={ticket.id}
                  ticketImage={ticket.image}
                  ns={ns}
                />
              </li>
              <li>
                <div>{t('balance')}</div>
                <CurrencySelector
                  cmsParams={cmsParams}
                  ns={ns}
                />
              </li>
            </ul>
          ) : null
        }
        effects={{
          circles: true,
          imageUrl: ticket.illustrationImage,
        }}
      />

      <Well
        theme="tickets"
        effects={{
          glow: true,
          circles: true,
          imageUrl: ticket.illustrationImage,
          lines: true,
        }}
      >
        <ListMissions layout="horizontal">
          {offers.map((offer) => (
            <JackpotPurchaseTicketItem
              ticket={ticket}
              key={offer.id}
              offer={offer}
              baseOffer={baseOffer}
              highlight={getOfferHighlight(offer, offers, t('bestValue'))}
              selected={false}
              onPurchaseButtonClick={() => {
                openOverlay(undefined, {
                  meta: {
                    ns,
                    cmsParams,
                    ticket: ticket,
                    offer: offer,
                    numberOfTickets: offer.minimumAmount,
                    ticketsInPool: ticketsInPool,
                    baseOffer: baseOffer,
                    offers: offers,
                  },
                });
              }}
              buttonDescription={
                offer.footerText
                  ? offer.footerText
                  : offer.stats.soldTickets
                    ? t('{{countFormatted}} ticketsPurchased', {
                        count: offer.stats.soldTickets,
                        countFormatted: integerFormatter.format(offer.stats.soldTickets),
                      })
                    : ''
              }
            />
          ))}
        </ListMissions>
      </Well>
    </React.Fragment>
  );
};
